// Dashboard-specific metrics (logic lives here; UI layout is in ~/react/ui/metrics)
import React from "react"
import { Button } from "~/react/ui/button"
import { CurrencyHelper } from "~/helpers/currency_helper"
import { campaigns_inbox_index_path, leads_path } from "@/lib/routes"
import {
  Metrics,
  MetricsItem,
  MetricsSeparator,
} from "~/react/ui/metrics"

declare const i18n: {
  t: (key: string) => string
}

interface DashboardMetricsProps extends React.HTMLAttributes<HTMLDivElement> {
  leads_count?: number
  leads_without_email?: number
  verified_leads?: number
  unread_inbox_messages?: number
}

const DashboardMetrics = React.forwardRef<HTMLDivElement, DashboardMetricsProps>(
  (
    {
      className,
      leads_count,
      leads_without_email,
      verified_leads,
      unread_inbox_messages,
      ...props
    },
    ref,
  ) => {
    // Guard against undefined and zero totals to avoid NaN/Infinity
    const totalLeads = leads_count ?? 0
    const withoutEmail = leads_without_email ?? 0
    const verified = verified_leads ?? 0

    const percentageOfLeadsWithEmail =
      totalLeads > 0 ? ((totalLeads - withoutEmail) / totalLeads) * 100 : 0
    const percentageOfVerifiedLeads =
      totalLeads > 0 ? (verified / totalLeads) * 100 : 0

    return (
      <Metrics ref={ref} className={className} {...props}>
        <MetricsItem
          icon="users"
          iconColor="var(--colors-secondary-600)"
          label={i18n.t("js.dashboard.headline_metrics.total_leads")}
          value={CurrencyHelper.numberWithDelimiter(totalLeads)}
        />

        <MetricsSeparator />

        <MetricsItem
          icon="at"
          iconColor="var(--colors-success-600)"
          label={i18n.t("js.dashboard.headline_metrics.with_email_address")}
          value={`${percentageOfLeadsWithEmail.toFixed(2)}%`}
          wide
        >
          {percentageOfLeadsWithEmail < 100 && (
            <Button size="xs" variant="default" asChild>
              <a href={leads_path({ email: "~" })}>
                {i18n.t("js.dashboard.headline_metrics.show_missing")}
              </a>
            </Button>
          )}
        </MetricsItem>

        <MetricsSeparator />

        <MetricsItem
          icon="check-circle"
          iconColor="var(--colors-success-600)"
          label={i18n.t("js.dashboard.headline_metrics.verified")}
          value={`${percentageOfVerifiedLeads.toFixed(2)}%`}
          wide
        >
          {percentageOfVerifiedLeads < 100 && (
            <Button size="xs" variant="default" asChild>
              <a
                href={leads_path({
                  verification_status: ["error", "unknown", "~"],
                })}
              >
                {i18n.t("js.dashboard.headline_metrics.show_unverified")}
              </a>
            </Button>
          )}
        </MetricsItem>

        <MetricsSeparator />

        <MetricsItem
          icon="inbox"
          iconColor="var(--colors-cyan-500)"
          label={i18n.t("js.dashboard.headline_metrics.new_replies")}
          value={CurrencyHelper.numberWithDelimiter(unread_inbox_messages ?? 0)}
        >
          {(unread_inbox_messages ?? 0) > 0 && (
            <Button size="xs" variant="default" asChild>
              <a href={campaigns_inbox_index_path()}>
                {i18n.t("js.dashboard.headline_metrics.go_to_inbox")}
              </a>
            </Button>
          )}
        </MetricsItem>
      </Metrics>
    )
  },
)

DashboardMetrics.displayName = "DashboardMetrics"

export { DashboardMetrics }

